import {Helper} from "../../modules/Helper.js";

const STEP_BASED_ACTION_KEYWORDS = ['course/create', 'course/edit'];

export class Form {
    constructor(element) {
        this.element = element;
        this.files = this.element.querySelectorAll('input[type="file"]');
        this.setListeners();

        // bind handlers so we can reuse safely if needed
        this.handleUnsavedChangeBound = this.handleUnsavedChange.bind(this);

        // listen to ANY user changes on this form
        this.element.addEventListener('input', this.handleUnsavedChangeBound);
        this.element.addEventListener('change', this.handleUnsavedChangeBound);

        if (this.isStepBased()) {
            this.element.setAttribute('unload-listen', '');
            window.addEventListener('beforeunload', this.preventUnsavedChanges);
        }
    }

    isStepBased() {
        const action = this.element.getAttribute('action') || '';
        return STEP_BASED_ACTION_KEYWORDS.some(key => {
            if (key instanceof RegExp) {
                return key.test(action);
            }
            return action.includes(key);
        });
    }

    setListeners() {
        this.element.addEventListener('submit', e => {
            e.preventDefault();
            if (this.element.getAttribute("action") === "false")
                return;
            this.toggleSubmit();
            this.logSubmission();
            this.sendData(new FormData(this.element))
        });
    }

    toggleSubmit(disabled = true) {
        if (disabled) {
            if (!this.element.hasAttribute('submitting')) {
                this.element.setAttribute('submitting', '');
                window.addEventListener('beforeunload', this.preventReload);
            }
        } else {
            this.element.removeAttribute('submitting');
            window.removeEventListener('beforeunload', this.preventReload);
        }

        this.element.querySelectorAll('[type="submit"]').forEach(item => {
            if (disabled) item.setAttribute('disabled', '')
            else item.removeAttribute('disabled');
        });
    }

    handleUnsavedChange() {
        const action = this.element.getAttribute('action') || '';
        const isCreateOrEdit = action.includes('create') || action.includes('edit');
        const isStepBased = this.isStepBased();

        // ignore non-create/edit and non step-based forms
        if (!isCreateOrEdit && !isStepBased) return;

        // step-based => treated as "changed" even if actual fields are untouched
        const hasChanges = this.hasChanges() || isStepBased;

        if (hasChanges) {
            if (!this.element.hasAttribute('unload-listen')) {
                this.element.setAttribute('unload-listen', '');
                window.addEventListener('beforeunload', this.preventUnsavedChanges);
            }
        } else {
            if (this.element.hasAttribute('unload-listen') && !isStepBased) {
                // only remove guard if it's NOT step-based – step-based stays protected
                this.element.removeAttribute('unload-listen');
                window.removeEventListener('beforeunload', this.preventUnsavedChanges);
            }
        }
    }

    hasChanges() {
        let changed = false;
        const elements = this.element.querySelectorAll('input, textarea, select');

        elements.forEach(field => {
            if (changed) return; // short-circuit

            if (field.type === 'checkbox' || field.type === 'radio') {
                if (field.checked !== field.defaultChecked) changed = true;
            } else if (field.tagName === 'SELECT') {
                const options = Array.from(field.options);
                options.some(opt => {
                    if (opt.selected !== opt.defaultSelected) {
                        changed = true;
                        return true;
                    }
                });
            } else if (field.type === 'file') {
                if (field.files && field.files.length > 0) changed = true;
            } else {
                if (field.value !== field.defaultValue) changed = true;
            }
        });

        return changed;
    }

    sendData(data) {
        let FORM = this;
        let url = window.location.protocol + "//" + window.location.hostname + ":" + window.location.port;
        if (this.element.hasAttribute('action') && this.element.getAttribute('action') !== "") {
            if (this.element.getAttribute('action').startsWith('http')) {
                url = this.element.getAttribute('action');
            } else {
                url += this.element.getAttribute('action');
            }
        } else {
            url += "/form/store";
        }

        let xhr = new XMLHttpRequest();

        if (!this.checkFiles()) {
            this.createPopup('Invalid files', 'error');
            return;
        }

        if (this.hasFiles(this.element)) {
            const totalSize = this.calculateTotalFileSize();
            xhr.upload.onprogress = function (event) {
                let percentComplete = 0;
                if (event.lengthComputable) {
                    percentComplete = (event.loaded / event.total) * 100;
                } else {
                    percentComplete = Math.max(0, Math.min((event.loaded / totalSize) * 100, 100));
                }
                FORM.updateProgressPopup(`Upload Progress: ${percentComplete.toFixed(2)}%`);
            };
            xhr.upload.onloadend = (pe) => {
                if (this.progressPopup)
                    setTimeout(() => {
                        this.progressPopup.hideToast();
                    }, 1000)
            };
        }

        xhr.onreadystatechange = function () {
            if (this.readyState === XMLHttpRequest.DONE) {
                let response = {success: false, reason: 'An error occurred'};
                try {
                    response = JSON.parse(this.responseText);
                } catch (e) {
                    FORM.createPopup(e, 'error')
                }
                switch (this.status) {
                    case 200:
                        if (response.success) {
                            FORM.element.removeAttribute('unload-listen');
                            window.removeEventListener('beforeunload', FORM.preventReload);
                            window.removeEventListener('beforeunload', FORM.preventUnsavedChanges);
                            if (response.redirect) {
                                FORM.createPopup("Operation successful", 'success')
                                setTimeout(() => {
                                    window.location.replace(response.redirect);
                                }, 1000)
                                return;
                            }


                            if (response.download) {
                                console.log(response);
                                const link = document.createElement('a');
                                link.href = response.download;
                                link.download = response.name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }
                            FORM.createPopup("Success", 'success')
                            if (response.remove) {
                                FORM.element.closest(response.remove)?.remove();
                            }
                            if (response.refresh)
                                window.location.reload();

                            FORM.reset();
                        } else {
                            FORM.createPopup(response.reason, 'error')
                        }
                        FORM.toggleSubmit(false);
                        break;
                    default:
                        if (response.success === false) {
                            FORM.createPopup(response.reason, 'error')
                        }
                        FORM.toggleSubmit(false);
                }
            }
        };
        xhr.open("POST", url);
        xhr.send(data);
    }

    reset() {
        //this.element.reset();
        let progressBar = this.element.querySelector('#progressbar');
        if (progressBar) progressBar.classList.remove('active');
        if (progressBar) progressBar.classList.remove('completed');
        this.toggleSubmit(false);
    }

    createPopup(content, type) {
        Toastify({
            text: content,
            duration: 5000,
            close: true,
            gravity: "bottom", // `top` or `bottom`
            position: "right", // `left`, `center` or `right`
            stopOnFocus: true, // Prevents dismissing of toast on hover
            style: colors[type],
        }).showToast();
    }

    handleFiles() {
        console.error('Create handler');
    }

    checkFiles() {
        let valid = [];
        let inputs = Array.from(this.element.querySelectorAll('input[type="file"]'));
        inputs.some(file => {
            if (file.hasAttribute('multiple')) {
                let max = file.getAttribute('multiple');
                console.log(max);
                if (max > 0 && file.files.length > max) {
                    this.createPopup(`You can only upload ${max} files`, 'error');
                    valid.push(false)
                    return;
                }
                valid.push(true)
            } else {
                valid.push(file.files.length <= 1);
            }
        });
        console.log(valid);
        console.log(valid.length, inputs.length, Helper.allTrue(valid));
        return valid.length === inputs.length && Helper.allTrue(valid);
    }

    logSubmission() {
        return;
        //@TODO handle logging of form submission
        let xhr = new XMLHttpRequest();
        xhr.onreadystatechange = function () {
            if (this.readyState === XMLHttpRequest.DONE && this.status === 200) console.log("ok");
        };
        xhr.open("POST", "/form/submission");
        xhr.send(new FormData(this.element));
    }

    preventReload(e) {
        e.preventDefault();
        return "Form is submitting, please wait!";
    }

    preventUnsavedChanges(e) {
        e.preventDefault();
        return "You have unsaved changes, are you sure you want to leave?";
    }

    hasFiles(form) {
        let hasFiles = false;
        console.log(form);
        Array.from(form.querySelectorAll('input[type="file"]')).some(file => {
            if (file.files.length > 0) {
                hasFiles = true;
                return true;
            }
        });
        return hasFiles;
    }

    calculateTotalFileSize() {
        let totalSize = 0;
        this.files.forEach(fileInput => {
            Array.from(fileInput.files).forEach(file => {
                totalSize += file.size;
            });
        });
        return totalSize;
    }

    updateProgressPopup(content) {
        if (!this.progressPopup) {
            this.progressPopup = Toastify({
                text: content,
                duration: -1, // Make it persistent
                close: false,
                gravity: "bottom",
                position: "right",
                stopOnFocus: false,
                style: colors['info'],
            }).showToast();
        } else {
            this.progressPopup.toastElement.innerText = content;
        }
    }
}


const colors = {
    'success': {
        background: '#12e298',
        color: '#000'
    },
    'error': {
        background: '#e21212',
        color: '#fff'
    },
    'info': {
        background: '#386DF1',
        color: '#000'
    }
}